import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col, Badge } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Checkbox from "@material-ui/core/Checkbox";
import { pdfOrdenCompraPorId } from "./OrdenesCorte/ordenCompraExternaPDF";

// (2026-08) Órdenes de compra de producto terminado (órdenes de corte externo):
// lo que se le pidió al proveedor, la tela que se le entregó y las prendas que
// ya regresó.
function ReporteOrdenesCompraPT() {
  const { user } = useContext(AuthContext);
  const URL_ORDENES_CORTE_EXTERNO = process.env.REACT_APP_URL_ORDENES_CORTE_EXTERNO;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 60);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(inicio).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );

  const [ordenes, setOrdenes] = useState([]);
  // (2026-09) varios proveedores a la vez, igual que en ReporteTelas
  const [selectedProveedores, setSelectedProveedores] = useState([]);
  // (2026-09) filtro por cliente del pedido
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedTela, setSelectedTela] = useState("");
  const [cargando, setCargando] = useState(false);

  // (2026-09) cliente con el subcliente entre paréntesis, como en ListadoPedidos
  // (2026-10) y la subárea de la orden, también entre paréntesis
  const clienteConSub = (r) => {
    const conDato = (v) => {
      const t = String(v ?? "").trim();
      return t && t.toUpperCase() !== "NA" ? t : "";
    };
    const sub = conDato(r?.subCliente);
    const subArea = conDato(r?.subArea);
    return [String(r?.cliente || "").trim(), sub && `(${sub})`, subArea && `(${subArea})`]
      .filter(Boolean)
      .join(" ");
  };

  // (2026-10) El mismo PDF de la orden de compra que en ListadoOrdenesCorteExterno
  const verOrdenCompra = async (o) => {
    const res = await Swal.fire({
      title: "¿Separar por área?",
      text: "Si dices Sí, cada área tendrá su propia banda en el PDF.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, separar",
      cancelButtonText: "No, junto",
    });
    if (res.dismiss && res.dismiss !== Swal.DismissReason.cancel) return;
    pdfOrdenCompraPorId(o._id, { separarPorArea: res.isConfirmed });
  };

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  const arr = (d) => (Array.isArray(d) ? d : []);
  const num = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  const mts = (n) =>
    `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
      Number(n || 0)
    )} m`;

  useEffect(() => {
    setCargando(true);
    axios
      .get(
        `${URL_ORDENES_CORTE_EXTERNO}ReportePT/${selectedFechaInicio}/${selectedFechaFin}`,
        { headers }
      )
      .then((r) => {
        setOrdenes(arr(r.data));
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setCargando(false);
        Swal.fire("Error", "No se pudo cargar el reporte de órdenes de compra", "error");
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  // (2026-09) Los combos salen de las órdenes del periodo, no del catálogo
  // completo: así sólo se listan proveedores y clientes que sí tienen órdenes.
  const proveedoresLista = useMemo(() => {
    const m = new Map();
    ordenes.forEach((o) => {
      const id = String(o.idProveedor || "");
      if (id && !m.has(id)) {
        m.set(id, { _id: id, nombre: String(o.proveedor || "").trim() || id });
      }
    });
    return [...m.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [ordenes]);

  const clientesLista = useMemo(() => {
    const s = new Set();
    ordenes.forEach((o) => {
      const c = String(o.cliente || "").trim();
      if (c) s.add(c);
    });
    return [...s].sort((a, b) => a.localeCompare(b, "es"));
  }, [ordenes]);

  // (2026-09) El status de tela sale de la orden y puede ser Completo,
  // Pendiente, Incompleto o "No llevara": el combo se arma con los que existan.
  const statusTelaLista = useMemo(() => {
    const s = new Set();
    ordenes.forEach((o) => {
      const t = String(o.requiereTela || "").trim();
      if (t) s.add(t);
    });
    return [...s].sort((a, b) => a.localeCompare(b, "es"));
  }, [ordenes]);

  const colorStatusTela = (v) => {
    const t = String(v || "").toUpperCase();
    if (t === "COMPLETO") return "success";
    if (t.startsWith("NO LLEVAR")) return "secondary";
    if (t === "INCOMPLETO") return "danger";
    return "warning";
  };

  // si al cambiar las fechas lo seleccionado ya no existe, se vuelve a "Todos"
  useEffect(() => {
    setSelectedProveedores((prev) =>
      prev.filter((p) => proveedoresLista.some((x) => x._id === p._id))
    );
  }, [proveedoresLista]);

  useEffect(() => {
    if (selectedCliente && !clientesLista.includes(selectedCliente)) {
      setSelectedCliente("");
    }
  }, [clientesLista, selectedCliente]);

  useEffect(() => {
    if (selectedTela && !statusTelaLista.includes(selectedTela)) {
      setSelectedTela("");
    }
  }, [statusTelaLista, selectedTela]);

  const filtrados = useMemo(() => {
    const ids = selectedProveedores.map((p) => String(p._id));
    return ordenes.filter(
      (o) =>
        (ids.length === 0 || ids.includes(String(o.idProveedor))) &&
        (!selectedCliente ||
          String(o.cliente || "").trim() === selectedCliente) &&
        (!selectedTela || o.requiereTela === selectedTela)
    );
  }, [ordenes, selectedProveedores, selectedCliente, selectedTela]);

  const tot = useMemo(() => {
    const t = { piezas: 0, monto: 0, metros: 0, recibidas: 0 };
    filtrados.forEach((o) => {
      t.piezas += Number(o.piezas || 0);
      t.monto += Number(o.montoTotal || 0);
      t.metros += Number(o.metrosEntregados || 0);
      t.recibidas += Number(o.recibidas || 0);
    });
    t.avance = t.piezas > 0 ? (t.recibidas / t.piezas) * 100 : 0;
    return t;
  }, [filtrados]);

  const listaOC = useMemo(
    () =>
      filtrados
        .slice(0, 4)
        .map((o) => `#${o.oc}`)
        .join(", ") + (filtrados.length > 4 ? "..." : ""),
    [filtrados]
  );

  // ---------------- PDF ----------------
  const PDFReporte = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay órdenes en el periodo/filtros", "warning");
    }

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const ML = 10;
    const usableW = pageW - ML * 2;

    const azulMarino = [23, 37, 74];
    const azul = [46, 117, 182];
    const morado = [124, 58, 200];
    const verde = [22, 101, 52];
    const verdeClaro = [216, 240, 224];
    const naranja = [180, 100, 20];
    const naranjaClaro = [255, 244, 214];
    const gris = [110, 120, 135];
    const bordeGris = [210, 216, 224];

    // ---- Banda de título
    doc.setFillColor(...azulMarino);
    doc.roundedRect(ML, 8, usableW, 18, 2.5, 2.5, "F");
    doc.setTextColor(255).setFont(undefined, "bold").setFontSize(14);
    doc.text("ESPECIFICACIÓN DE REPORTE: ÓRDENES DE COMPRA", ML + 5, 16);
    doc.setFont(undefined, "normal").setFontSize(8.5);
    doc.text(
      `Producto Terminado | Estructura Integrada de Control de Telas y Avance de Recepción  ·  ${moment(
        selectedFechaInicio
      ).format("DD/MM/YYYY")} - ${moment(selectedFechaFin).format("DD/MM/YYYY")}`,
      ML + 5,
      21.5
    );
    doc.setTextColor(0);

    // ---- Banda de sección
    let y = 30;
    doc.setFillColor(...azulMarino);
    doc.rect(ML, y, usableW, 7, "F");
    doc.setFont(undefined, "bold").setFontSize(9.5).setTextColor(255);
    doc.text(
      "ÓRDENES DE COMPRA, CONSUMO DE TELA Y RECEPCIÓN DE PRENDAS",
      ML + 3,
      y + 4.8
    );
    doc.setTextColor(0);
    y += 7;

    doc.autoTable({
      startY: y,
      margin: { left: ML, right: ML, top: 12, bottom: 14 },
      tableWidth: usableW,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.6,
        valign: "middle",
        lineColor: bordeGris,
        lineWidth: 0.15,
        textColor: 40,
      },
      headStyles: {
        fillColor: [240, 243, 247],
        textColor: azulMarino,
        fontStyle: "bold",
        fontSize: 7.5,
        halign: "center",
      },
      alternateRowStyles: { fillColor: [250, 251, 253] },
      head: [[
        "NO. OC", "PROVEEDOR", "CLIENTE", "CANT. PIEZAS", "MONTO UNITARIO",
        "MONTO TOTAL", "STATUS TELA", "TELA ASIGNADA", "MTS. ENTREGADOS",
        "PRENDAS RECIBIDAS / %",
      ]],
      columnStyles: {
        0: { cellWidth: 16, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 48, fontStyle: "bold" },
        2: { cellWidth: 46 },
        3: { cellWidth: 20, halign: "center", fontStyle: "bold" },
        4: { cellWidth: 24, halign: "right" },
        5: { cellWidth: 26, halign: "right" },
        6: { cellWidth: 22, halign: "center" },
        7: { cellWidth: 34, textColor: azul, fontStyle: "bold" },
        8: { cellWidth: 24, halign: "center" },
        9: { halign: "center" },
      },
      body: filtrados.map((o) => [
        `#${o.oc}`,
        o.proveedor || "-",
        clienteConSub(o) || "-",
        num(o.piezas),
        money(o.montoUnitario),
        money(o.montoTotal),
        o.requiereTela,
        o.telaAsignada || "-",
        o.metrosEntregados > 0 ? mts(o.metrosEntregados) : "0 m\n(Pendiente)",
        `${num(o.recibidas)} (${Number(o.avance || 0).toFixed(0)}%)`,
      ]),
      foot: [[
        { content: "TOTALES:", colSpan: 3, styles: { halign: "right" } },
        num(tot.piezas),
        "",
        money(tot.monto),
        { content: "TOTAL MTS. ENTREGADOS:", colSpan: 2, styles: { halign: "right" } },
        mts(tot.metros),
        `${num(tot.recibidas)} (${tot.avance.toFixed(0)}%)`,
      ]],
      footStyles: {
        fillColor: azulMarino,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      didParseCell: (h) => {
        if (h.section !== "body") return;
        const o = filtrados[h.row.index];
        if (!o) return;
        if (h.column.index === 6) {
          // (2026-09) el status viene de la orden: puede ser Completo,
          // Pendiente, Incompleto o "No llevara"
          const st = String(o.requiereTela || "").toUpperCase();
          if (st === "COMPLETO") {
            h.cell.styles.fillColor = verdeClaro;
            h.cell.styles.textColor = verde;
          } else if (st.startsWith("NO LLEVAR")) {
            h.cell.styles.textColor = gris;
          } else {
            h.cell.styles.fillColor = naranjaClaro;
            h.cell.styles.textColor = naranja;
          }
          h.cell.styles.fontStyle = "bold";
        }
        if (h.column.index === 8) {
          h.cell.styles.textColor = o.metrosEntregados > 0 ? verde : naranja;
          h.cell.styles.fontStyle = "bold";
        }
        if (h.column.index === 9 && o.recibidas > 0) {
          h.cell.styles.textColor = verde;
          h.cell.styles.fontStyle = "bold";
        }
      },
    });

    // ---- Tarjetas de indicadores
    let yT = (doc.lastAutoTable?.finalY || y) + 6;
    const tarjH = 22;
    if (yT + tarjH > pageH - 14) {
      doc.addPage();
      yT = 16;
    }

    const tarjetas = [
      {
        titulo: "TOTAL DE ÓRDENES (OC)",
        valor: String(filtrados.length),
        pie: `Órdenes Registradas (${listaOC || "-"})`,
        color: azul,
      },
      {
        titulo: "CANTIDAD TOTAL SOLICITADA",
        valor: num(tot.piezas),
        pie: "Piezas Totales de Producto Terminado",
        color: morado,
      },
      {
        titulo: "PRENDAS RECIBIDAS TOTALES",
        valor: `${num(tot.recibidas)} (${tot.avance.toFixed(0)}%)`,
        pie: "Pendiente de Entrega por Proveedores",
        color: verde,
      },
    ];

    const gap = 5;
    const tarjW = (usableW - gap * (tarjetas.length - 1)) / tarjetas.length;
    tarjetas.forEach((t, i) => {
      const x = ML + i * (tarjW + gap);
      doc.setFillColor(252, 253, 255).setDrawColor(...bordeGris).setLineWidth(0.25);
      doc.roundedRect(x, yT, tarjW, tarjH, 2, 2, "FD");
      doc.setFillColor(...t.color);
      doc.rect(x + 1, yT, tarjW - 2, 1.4, "F");
      doc.setFont(undefined, "bold").setFontSize(8).setTextColor(...gris);
      doc.text(t.titulo, x + tarjW / 2, yT + 6.5, { align: "center" });
      doc.setFontSize(16).setTextColor(...t.color);
      doc.text(t.valor, x + tarjW / 2, yT + 14.5, { align: "center" });
      doc.setFont(undefined, "normal").setFontSize(7).setTextColor(...gris);
      doc.text(doc.splitTextToSize(t.pie, tarjW - 8)[0], x + tarjW / 2, yT + 19.5, {
        align: "center",
      });
      doc.setTextColor(0);
    });

    // ---- Pie
    const paginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setFont(undefined, "normal").setFontSize(7.5).setTextColor(...gris);
      doc.text(
        "LANGIANI ERP - Órdenes de Compra de Producto Terminado",
        ML,
        pageH - 5.5
      );
      doc.text(`Página ${p} de ${paginas}`, pageW - ML, pageH - 5.5, { align: "right" });
      doc.setTextColor(0);
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  const excel = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay órdenes en el periodo/filtros", "warning");
    }
    const option = {};
    option.fileName = "ReporteOrdenesCompraPT";
    option.datas = [
      {
        sheetData: filtrados.map((o) => ({
          OC: o.oc,
          Fecha: o.fecha,
          Proveedor: o.proveedor,
          Cliente: clienteConSub(o),
          Piezas: o.piezas,
          MontoUnitario: o.montoUnitario,
          MontoTotal: o.montoTotal,
          RequiereTela: o.requiereTela,
          TelaAsignada: o.telaAsignada,
          MetrosEntregados: o.metrosEntregados,
          Recibidas: o.recibidas,
          Avance: Number(o.avance || 0).toFixed(1),
        })),
        sheetName: "Ordenes PT",
        sheetFilter: ["OC","Fecha","Proveedor","Cliente","Piezas","MontoUnitario","MontoTotal","RequiereTela","TelaAsignada","MetrosEntregados","Recibidas","Avance"],
        sheetHeader: ["No. OC","Fecha","Proveedor","Cliente","Cant. Piezas","Monto Unitario","Monto Total","Status Tela","Tela Asignada","Mts. Entregados","Prendas Recibidas","% Avance"],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_produccion ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuProduccion" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFReporte}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Órdenes de Compra Producto Terminado</h3>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label>Proveedor</Label>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={proveedoresLista}
                value={selectedProveedores}
                getOptionLabel={(o) => o?.nombre || ""}
                getOptionSelected={(o, v) => String(o._id) === String(v._id)}
                onChange={(e, val) => setSelectedProveedores(val || [])}
                renderOption={(option, { selected }) => (
                  <>
                    <Checkbox size="small" checked={selected} color="primary" />
                    {option.nombre}
                  </>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={
                      selectedProveedores.length ? "" : "Todos los proveedores"
                    }
                  />
                )}
              />
            </Col>
            <Col md={3}>
              <Label>Cliente</Label>
              <Input
                type="select"
                value={selectedCliente}
                onChange={(e) => setSelectedCliente(e.target.value)}
              >
                <option value="">Todos</option>
                {clientesLista.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Status Tela</Label>
              <Input
                type="select"
                value={selectedTela}
                onChange={(e) => setSelectedTela(e.target.value)}
              >
                <option value="">Todos</option>
                {statusTelaLista.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          <br />

          <Row>
            <Col md={4}>
              <div className="card p-2 text-center" style={{ borderTop: "3px solid #2E75B6" }}>
                <small className="text-muted">TOTAL DE ÓRDENES (OC)</small>
                <h4 style={{ color: "#2E75B6" }}>{filtrados.length}</h4>
                <small className="text-muted">Órdenes Registradas {listaOC}</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="card p-2 text-center" style={{ borderTop: "3px solid #7C3AC8" }}>
                <small className="text-muted">CANTIDAD TOTAL SOLICITADA</small>
                <h4 style={{ color: "#7C3AC8" }}>{num(tot.piezas)}</h4>
                <small className="text-muted">Piezas Totales de Producto Terminado</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="card p-2 text-center" style={{ borderTop: "3px solid #166534" }}>
                <small className="text-muted">PRENDAS RECIBIDAS TOTALES</small>
                <h4 style={{ color: "#166534" }}>
                  {num(tot.recibidas)} ({tot.avance.toFixed(0)}%)
                </h4>
                <small className="text-muted">Pendiente de Entrega por Proveedores</small>
              </div>
            </Col>
          </Row>

          <br />
          {cargando ? <h5 align="center">Cargando...</h5> : null}

          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th className="text-center">No. OC</th>
                <th>Proveedor</th>
                <th>Cliente</th>
                <th className="text-center">Cant. Piezas</th>
                <th className="text-end">Monto Unitario</th>
                <th className="text-end">Monto Total</th>
                <th className="text-center">Status Tela</th>
                <th>Tela Asignada</th>
                <th className="text-center">Mts. Entregados</th>
                <th className="text-center">Prendas Recibidas / %</th>
                <th className="text-center">OC</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length ? (
                filtrados.map((o) => (
                  <tr key={o._id}>
                    <td className="text-center negrita">#{o.oc}</td>
                    <td className="negrita">{o.proveedor}</td>
                    <td>{clienteConSub(o)}</td>
                    <td className="text-center negrita">{num(o.piezas)}</td>
                    <td className="text-end">{money(o.montoUnitario)}</td>
                    <td className="text-end">{money(o.montoTotal)}</td>
                    <td className="text-center">
                      <Badge color={colorStatusTela(o.requiereTela)}>
                        {o.requiereTela}
                      </Badge>
                    </td>
                    <td style={{ color: "#2E75B6" }} className="negrita">
                      {o.telaAsignada || "-"}
                    </td>
                    <td
                      className="text-center negrita"
                      style={{ color: o.metrosEntregados > 0 ? "#166534" : "#B46414" }}
                    >
                      {o.metrosEntregados > 0 ? mts(o.metrosEntregados) : "0 m (Pend.)"}
                    </td>
                    <td
                      className="text-center"
                      style={o.recibidas > 0 ? { color: "#166534", fontWeight: "bold" } : undefined}
                    >
                      {num(o.recibidas)} ({Number(o.avance || 0).toFixed(0)}%)
                    </td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        color="danger"
                        title="Ver la orden de compra"
                        onClick={() => verOrdenCompra(o)}
                      >
                        <i className="far fa-file-pdf"></i>
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="text-center text-muted">
                    No hay órdenes en el periodo seleccionado
                  </td>
                </tr>
              )}
              {filtrados.length ? (
                <tr>
                  <td colSpan="3" className="text-end negrita">
                    TOTALES:
                  </td>
                  <td className="text-center negrita">{num(tot.piezas)}</td>
                  <td></td>
                  <td className="text-end negrita">{money(tot.monto)}</td>
                  <td colSpan="2" className="text-end negrita">
                    TOTAL MTS. ENTREGADOS:
                  </td>
                  <td className="text-center negrita">{mts(tot.metros)}</td>
                  <td className="text-center negrita">
                    {num(tot.recibidas)} ({tot.avance.toFixed(0)}%)
                  </td>
                  <td></td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          <br />
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default ReporteOrdenesCompraPT;
